import { getTextRasterAssetKey, getTextRenderMode } from "./textRaster.js";
import { getLayerTextRender } from "./bakedIllustration.js";
/** Legacy magenta-keyed assets (pre-U12) — still keyed client-side if present. */
export const SURF_CUTOUT_CHROMA_KEY = "#FF00FF";
/** Deck background color from palette (fallback when no bitmap sampled). */
export function deckBackgroundColor(theme) {
    return normalizeHex(theme.palette.surface);
}
/**
 * Actual slide background color: dominant color sampled from the rendered background
 * bitmap when available (U12), else palette.surface. The illustration bg ignores
 * palette.surface, so matching the widget to it requires the sampled color.
 */
export function slideBackdropColor(slide, theme) {
    const sampled = slide.background.dominantColor;
    if (typeof sampled === "string" && /^#[0-9A-Fa-f]{6}$/.test(sampled.trim())) {
        return normalizeHex(sampled);
    }
    const manual = slide.background.solidColor;
    if (typeof manual === "string" && /^#[0-9A-Fa-f]{6}$/.test(manual.trim())) {
        return normalizeHex(manual);
    }
    return deckBackgroundColor(theme);
}
export function solidBackdropPromptLines(hex) {
    const color = normalizeHex(hex);
    return [
        `Fill the entire canvas with one uniform flat solid color exactly ${color}.`,
        "No gradient, no texture, no checkerboard, no transparency simulation.",
        `Subject and any baked text sit on this solid ${color} background.`,
    ];
}
/** @deprecated U12 — generation uses palette.surface; kept for legacy prompt fallbacks. */
export function cutoutChromaKeyPromptLines() {
    return [
        `Fill all unused canvas area with flat solid chroma key ${SURF_CUTOUT_CHROMA_KEY} only.`,
        "No gradient, no texture, no checkerboard, no gray/white faux-transparency grids.",
        `Place the subject on ${SURF_CUTOUT_CHROMA_KEY}; unused areas stay exactly that color for post keying.`,
    ];
}
function normalizeHex(hex) {
    const raw = hex.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(raw))
        return raw.toUpperCase();
    if (/^#[0-9A-Fa-f]{3}$/.test(raw)) {
        const h = raw.slice(1);
        return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toUpperCase();
    }
    return "#FFFFFF";
}
/** UI-facing raster background mode mapped to cutout / useDeckBackdrop flags. */
export function getRasterBackgroundMode(layer) {
    if (layer.style?.cutout === true)
        return "transparent";
    if (layer.style?.useDeckBackdrop === false)
        return "white";
    return "slide";
}
export function setRasterBackgroundMode(layer, mode) {
    const base = { ...layer.style, rasterStale: true };
    switch (mode) {
        case "transparent":
            return {
                ...layer,
                style: { ...base, cutout: true, useDeckBackdrop: true },
            };
        case "white":
            return {
                ...layer,
                style: { ...base, cutout: undefined, useDeckBackdrop: false },
            };
    }
    return {
        ...layer,
        style: { ...base, cutout: undefined, useDeckBackdrop: true },
    };
}
/** Whether the widget forces the slide background color as its raster backdrop (default on). */
export function usesDeckBackdrop(layer) {
    return layer.style?.useDeckBackdrop !== false;
}
/** Distinct color options from the deck style (palette + background/text arrays) for pickers. */
export function styleColorPalette(theme) {
    const raw = [
        theme.palette.surface,
        theme.palette.text,
        theme.palette.accent,
        theme.palette.muted,
        ...(theme.paletteBackground ?? []),
        ...(theme.paletteText ?? []),
    ];
    const seen = new Set();
    const out = [];
    for (const c of raw) {
        if (typeof c !== "string")
            continue;
        const hex = normalizeHex(c);
        if (!seen.has(hex)) {
            seen.add(hex);
            out.push(hex);
        }
    }
    return out;
}
/** Foreground/text colors only (no surface/background) for typography pickers. */
export function styleTextPalette(theme) {
    const raw = [
        theme.palette.text,
        theme.palette.accent,
        theme.palette.muted,
        ...(theme.paletteText ?? []),
    ];
    const seen = new Set();
    const out = [];
    for (const c of raw) {
        if (typeof c !== "string")
            continue;
        const hex = normalizeHex(c);
        if (!seen.has(hex)) {
            seen.add(hex);
            out.push(hex);
        }
    }
    return out;
}
/** Per-widget manual override for the illustration backdrop color (palette or hex). */
export function getRasterBackdropOverride(layer) {
    const raw = layer.style?.rasterBackdropOverride;
    return typeof raw === "string" && /^#[0-9A-Fa-f]{6}$/.test(raw.trim())
        ? normalizeHex(raw)
        : undefined;
}
/**
 * Opaque backdrop for raster generation: manual override > sampled slide bg color >
 * neutral white. The override lets the user pick the illustration background.
 */
export function resolveRasterBackdropColor(layer, slide, theme) {
    const override = getRasterBackdropOverride(layer);
    if (override)
        return override;
    if (!usesDeckBackdrop(layer))
        return normalizeHex("#FFFFFF");
    return slideBackdropColor(slide, theme);
}
export function getRasterBackdrop(layer, theme) {
    const raw = layer.style?.rasterBackdrop;
    if (typeof raw === "string" && raw.trim()) {
        return normalizeHex(raw);
    }
    return deckBackgroundColor(theme);
}
export function parseChromaKeyRgb(hex) {
    const h = normalizeHex(hex).slice(1);
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
}
/** Client chroma-key when cutout floats above the slide background (U12). */
export function needsRasterChromaKeyPrep(layer) {
    if (layer.style?.cutout !== true)
        return false;
    if (layer.type === "image") {
        const key = layer.content?.trim();
        return Boolean(key && key.includes("/"));
    }
    if (layer.type === "text" && getTextRenderMode(layer) === "raster") {
        return Boolean(getTextRasterAssetKey(layer));
    }
    return false;
}
export function chromaKeyHexForLayer(layer, theme) {
    return getRasterBackdrop(layer, theme);
}
/** Solid panel under raster (opaque mode); false when cutout applies client keying. */
export function usesRasterSolidPanel(layer) {
    if (layer.style?.cutout === true)
        return false;
    if (layer.type === "text" && getTextRenderMode(layer) === "raster") {
        return Boolean(getTextRasterAssetKey(layer));
    }
    if (layer.type === "image" && getLayerTextRender(layer) === "in_image") {
        return Boolean(layer.content?.trim());
    }
    return false;
}
/** @deprecated use needsRasterChromaKeyPrep */
export function needsCutoutChromaKeyPrep(layer) {
    return needsRasterChromaKeyPrep(layer);
}
